import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/lib/apiClient';
import { invalidateHealthQueries, invalidateStartAlignmentQueries } from '@/lib/queryClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { toDateInputValue, DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import {
  PHASE_META, weightedOverall, actualStartPatch,
  type ProjectPhase, type PhaseKey,
} from '@/lib/schedule';

type PhaseForm = {
  originalStart: string; plannedStart: string; originalEnd: string;
  actualStart: string; actualEnd: string;
  isProjectedEnd: boolean;
  completionPct: string; budgetLaborHours: string;
};

const EMPTY: PhaseForm = {
  originalStart: '', plannedStart: '', originalEnd: '', actualStart: '', actualEnd: '',
  isProjectedEnd: true, completionPct: '', budgetLaborHours: '',
};

function seed(phase: ProjectPhase | undefined): PhaseForm {
  if (!phase) return { ...EMPTY };
  return {
    originalStart: toDateInputValue(phase.originalStart),
    plannedStart: toDateInputValue(phase.plannedStart),
    originalEnd: toDateInputValue(phase.originalEnd),
    actualStart: toDateInputValue(phase.actualStart),
    actualEnd: toDateInputValue(phase.actualEnd),
    isProjectedEnd: phase.isProjectedEnd,
    completionPct: phase.completionPct != null ? String(phase.completionPct) : '',
    budgetLaborHours: phase.budgetLaborHours != null ? String(phase.budgetLaborHours) : '',
  };
}

const ne = (v: string) => (v.trim() === '' ? null : v);
const nn = (v: string) => (v.trim() === '' ? null : Number(v));

/**
 * Right-side editor for the per-phase schedule. Fab jobs edit fabrication +
 * installation; other divisions edit a single project phase. Saves via
 * PATCH /projects/:id/phases (partial per field) and refreshes the project.
 */
export default function PhaseScheduleDrawer({
  projectId, phaseKeys, phases, onClose,
}: {
  projectId: number;
  phaseKeys: PhaseKey[];
  phases: ProjectPhase[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { onEscape: onClose });

  const byKey = useMemo(() => new Map(phases.map((p) => [p.phase, p])), [phases]);
  const [forms, setForms] = useState<Record<string, PhaseForm>>(() => {
    const o: Record<string, PhaseForm> = {};
    for (const k of phaseKeys) o[k] = seed(byKey.get(k));
    return o;
  });
  /**
   * What each field held when the drawer opened, so `actualStart` can be sent
   * only when the user actually TOUCHED it.
   *
   * ⚠️ Without this, an untouched field is a STALE ECHO that reads as an override.
   * `actualStart` is Paycor-derived on a 'project' phase and the nightly sync can
   * move it while this drawer sits open. Re-sending the page-load value then
   * differs from what is now stored, so the server — which decides provenance by
   * comparing values — would stamp it 'manual', restore the old date, and opt the
   * phase out of the feed permanently, all because somebody saved a budget-hours
   * edit. Frozen at mount by design: it is the snapshot the user was shown.
   */
  const [openedWith] = useState<Record<string, PhaseForm>>(() => {
    const o: Record<string, PhaseForm> = {};
    for (const k of phaseKeys) o[k] = seed(byKey.get(k));
    return o;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upd = (k: string, field: keyof PhaseForm, value: string | boolean) =>
    setForms((prev) => ({ ...prev, [k]: { ...prev[k], [field]: value } }));

  // Live overall-% preview.
  const preview = weightedOverall(
    phaseKeys.map((k) => ({
      phase: k,
      completionPct: nn(forms[k].completionPct),
      budgetLaborHours: nn(forms[k].budgetLaborHours),
    })),
  );

  const onSave = async () => {
    setSaving(true); setError(null);
    try {
      const payload = phaseKeys.map((k) => ({
        phase: k,
        originalStart: ne(forms[k].originalStart),
        plannedStart: ne(forms[k].plannedStart),
        originalEnd: ne(forms[k].originalEnd),
        actualEnd: ne(forms[k].actualEnd),
        isProjectedEnd: forms[k].isProjectedEnd,
        completionPct: nn(forms[k].completionPct),
        budgetLaborHours: nn(forms[k].budgetLaborHours),
        // ⚠️ `actualStart` is the ONE field sent only when the user TOUCHED it.
        // The rule and the reasoning live in actualStartPatch — it is subtle
        // enough to be worth a test, which is why it is a pure helper rather than
        // a condition inline here.
        ...actualStartPatch(forms[k].actualStart, openedWith[k].actualStart),
      }));
      await projectsApi.updatePhases(projectId, payload);
      // ⚠️ `upsertPhases` back-fills the LEGACY PROJECT DATES —
      // `syncLegacyScheduleDates` runs `UPDATE projects SET current_start_date,
      // original_start_date, estimated_completion`, which are the three columns
      // `deriveHealth` reads. So saving here can move a job's health verdict,
      // and both Today pages have to hear about it. (Codex, P2 on #513.)
      await invalidateHealthQueries(qc, projectId);
      // …and the crew grid's copy of `current_start_date`, which is one half of the
      // start-mismatch flag (#385). invalidateHealthQueries clears `projects` and
      // `project`, not `manpower-project-options` — so without this, re-planning a
      // start leaves the badge on the grid asserting a disagreement the PM has
      // just resolved, or hiding one they have just created.
      await invalidateStartAlignmentQueries(qc, projectId);
      qc.invalidateQueries({ queryKey: ['kpi'] });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const field: React.CSSProperties = { display: 'flex', flexDirection: 'column' };
  const labelCss: React.CSSProperties = {
    font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)',
    textTransform: 'uppercase', color: 'var(--ew-n-500)', marginBottom: 5,
  };
  const inputCss: React.CSSProperties = {
    border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px',
    font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', width: '100%', background: '#fff', color: 'var(--ew-dark)',
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(54,54,54,0.32)', zIndex: 200 }} />
      <div ref={ref} role="dialog" aria-label="Edit schedule" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 540, maxWidth: '100vw', background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--ew-n-200)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div>
            <div style={{ font: 'var(--ew-fw-semibold) 15px/1.2 var(--ew-font)' }}>Edit Schedule</div>
            <div style={{ font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-500)', marginTop: 2 }}>
              Overall <b style={{ color: 'var(--ew-dark)' }}>{preview.overallPct}%</b> · weighted by budget hours
            </div>
          </div>
          <button onClick={onClose} aria-label="Close drawer" style={{ fontSize: 18, color: 'var(--ew-n-500)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
        </div>

        {error && <div style={{ margin: '10px 18px 0', padding: '8px 12px', background: 'var(--ew-danger-50)', color: 'var(--ew-danger)', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>{error}</div>}

        <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-5)', flex: 1 }}>
          {phaseKeys.map((k) => {
            const meta = PHASE_META[k];
            const f = forms[k];
            return (
              <div key={k} style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: 'var(--ew-s-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', marginBottom: 'var(--ew-s-3)' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: meta.tick }} />
                  <span style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-ui) var(--ew-font)' }}>{meta.label}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--ew-s-3) var(--ew-s-4)' }}>
                  {/*
                    Three starts, and the labels have to do the teaching (#380).
                    "Actual / Proj. Start" was the old single field, and its slash
                    is precisely the conflation this issue fixes: a projected start
                    is a plan and belongs in Planned Start, while Actual Start now
                    means work has genuinely begun — it is what drives the
                    In Progress badge. The end side keeps its slash, because there
                    the projection is flagged separately by the checkbox below.
                  */}
                  <label style={field}><span style={labelCss}>Original Start</span><input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inputCss} value={f.originalStart} onChange={(e) => upd(k, 'originalStart', e.target.value)} /></label>
                  <label style={field}><span style={labelCss}>Original End</span><input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inputCss} value={f.originalEnd} onChange={(e) => upd(k, 'originalEnd', e.target.value)} /></label>
                  <label style={field}><span style={labelCss}>Planned Start</span><input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inputCss} value={f.plannedStart} onChange={(e) => upd(k, 'plannedStart', e.target.value)} /></label>
                  <label style={field}><span style={labelCss}>Actual / Proj. End</span><input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inputCss} value={f.actualEnd} onChange={(e) => upd(k, 'actualEnd', e.target.value)} /></label>
                  <label style={field}>
                    <span style={labelCss}>Actual Start</span>
                    <input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} style={inputCss} value={f.actualStart} onChange={(e) => upd(k, 'actualStart', e.target.value)} />
                    <span style={{ marginTop: 4, font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-400)' }}>
                      {k === 'project'
                        ? 'Set from Paycor field hours. Editing it here overrides that for good.'
                        : 'When work actually began — leave blank until it does.'}
                    </span>
                  </label>
                  <label style={field}><span style={labelCss}>Completion %</span><input type="number" min={0} max={100} style={inputCss} value={f.completionPct} onChange={(e) => upd(k, 'completionPct', e.target.value)} /></label>
                  <label style={field}><span style={labelCss}>Budget Labor Hours</span><input type="number" min={0} style={inputCss} value={f.budgetLaborHours} onChange={(e) => upd(k, 'budgetLaborHours', e.target.value)} /></label>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 'var(--ew-s-3)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', color: 'var(--ew-n-500)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.isProjectedEnd} onChange={(e) => upd(k, 'isProjectedEnd', e.target.checked)} />
                  End date is a projection (shows a “(proj.)” tag until the phase completes)
                </label>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--ew-s-2)', padding: 'var(--ew-s-3) 18px', borderTop: '1px solid var(--ew-n-200)', position: 'sticky', bottom: 0, background: '#fff' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save Schedule'}</button>
        </div>
      </div>
    </>
  );
}
